import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { FieldArray, Form, Formik } from 'formik';
import moment from 'moment';
import { Box, IconButton, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import _ from 'lodash';

import { getWarehouseDocumentAction, getWarehouseDocumentState } from '../../../../../features/inventory/get_warehouse_document';
import useNotification from '../../../../../core/hooks/use_notification';
import { MISC_DATE_ISO_FORMAT, MISC_DATE_POLISH_DATE_ONLY_FORMAT } from '../../../../../core/constants';
import TextFieldFormik from '../../../../common/text_field';
import { SubmitButton } from '../../../../common/button';
import { confirmDeliveryDocumentAction, confirmDeliveryDocumentState } from '../../../../../features/inventory/confirm_delivery_document';
import { updateAndConfirmDeliveryDocumentAction, updateAndConfirmDeliveryDocumentState } from '../../../../../features/inventory/update_and_confirm_delivery_document';
import { confirmDeliveryDocumentNotificationData, getWarehouseDocumentNotificationData, updateAndConfirmDeliveryDocumentNotificationData } from '../../../../../core/utils/notification_utils';
import { ApiRequestStatusEnum } from '../../../../../core/enums/common/api';
import BottomActionBar from "../../../../common/bottom_action_bar";

const DetailsForm = ({ warehouseType, podId, hubId, popId, queryParams }) => {
    const { enqueueSnackbar } = useSnackbar();
    const dispatch = useDispatch();
    const { showNotification, showErrorMessage } = useNotification();
    const navigate = useNavigate();
    const location = useLocation();

    const getWarehouseDocument = useSelector(getWarehouseDocumentState);
    const confirmDeliveryDocument = useSelector(confirmDeliveryDocumentState);
    const updateAndConfirmDeliveryDocument = useSelector(updateAndConfirmDeliveryDocumentState);

    const initialValues = {
        productBatches: getWarehouseDocument?.data?.positions?.map(item => ({
            name: item?.product?.name,
            expiryDate: item?.expiryDate,
            netPrice: item?.netPrice?.toFixed(2),
            grossPrice: item?.grossPrice?.toFixed(2),
            productId: item?.product?.id,
            productBatchId: item?.productBatchId,
            quantity: item?.quantity,
            maxQuantity: item?.quantity
        })) || []
    };

    const schema = Yup.object().shape({
        productBatches: Yup.array().of(
            Yup.object().shape({
                productId: Yup.number().required(),
                productBatchId: Yup.string().required(),
                quantity: Yup.number().min(0).required()
            })
        )
    });

    const onRemoveProduct = (formik, index) => {
        const productBatches = [...formik.values.productBatches];
        const productBatch = productBatches[index];

        if (index !== -1) {
            productBatches.splice(index, 1, { ...productBatch, quantity: 0 });
            formik.setFieldValue('productBatches', productBatches);
        }
    };

    const onSubmit = (values) => {
        const shouldUpdate = values?.productBatches?.some(item => item?.quantity < item?.maxQuantity);

        if (shouldUpdate) {
            const form = { productBatches: values?.productBatches?.map(item => _.pick(item, ['productId', 'productBatchId', 'quantity'])) };
            const isEmptyDocument = form?.productBatches?.filter(item => item?.quantity > 0)?.length === 0;

            if (isEmptyDocument) {
                enqueueSnackbar('Brak pozycji na dokumencie', { variant: 'error' });
                return;
            }

            dispatch(updateAndConfirmDeliveryDocumentAction({
                id: getWarehouseDocument?.data?.id,
                warehouseType,
                podId,
                hubId,
                popId,
                form
            }))
                .then(response => {
                    showNotification((updateAndConfirmDeliveryDocumentNotificationData(response)));

                    if (response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED) {
                        dispatch(getWarehouseDocumentAction(getWarehouseDocument?.data?.id))
                            .then(response => {
                                showErrorMessage((getWarehouseDocumentNotificationData(response)));
                                response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED && navigate(location.pathname, {
                                    state: {
                                        queryParams,
                                        confirmed: true
                                    }
                                });
                            });
                    }
                });
        } else {
            dispatch(confirmDeliveryDocumentAction({
                id: getWarehouseDocument?.data?.id,
                warehouseType,
                podId,
                hubId,
                popId
            }))
                .then(response => {
                    showNotification((confirmDeliveryDocumentNotificationData(response)));

                    if (response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED) {
                        dispatch(getWarehouseDocumentAction(getWarehouseDocument?.data?.id))
                            .then(response => {
                                showErrorMessage((getWarehouseDocumentNotificationData(response)));
                                response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED && navigate(location.pathname, {
                                    state: {
                                        queryParams,
                                        confirmed: true
                                    }
                                });
                            });
                    }
                });
        }
    };

    return (
        <Formik
            initialValues={ initialValues }
            validationSchema={ schema }
            validateOnChange={ false }
            validateOnBlur={ false }
            onSubmit={ onSubmit }
            enableReinitialize
        >
            { (formik) => {
                const activeBatches = formik.values.productBatches?.filter(item => item?.quantity > 0) || [];
                const totalQuantity = formik.values.productBatches?.reduce((sum, item) => sum + (Number(item?.quantity) || 0), 0) || 0;
                const totalNet = formik.values.productBatches?.reduce((sum, item) => sum + ((Number(item?.quantity) || 0) * (Number(item?.netPrice) || 0)), 0) || 0;
                const totalGross = formik.values.productBatches?.reduce((sum, item) => sum + ((Number(item?.quantity) || 0) * (Number(item?.grossPrice) || 0)), 0) || 0;

                return (
                    <Box component={ Form } sx={ { display: 'flex', flexDirection: 'column', flexGrow: 1 } }>
                        <Stack spacing={ 4 } mb={ 4 } sx={ { flexGrow: 1 } }>
                            <Typography variant="h6" gutterBottom sx={ { fontWeight: 600, mb: 0 } }>
                                Edycja pozycji dokumentu (do zatwierdzenia)
                            </Typography>

                            <TableContainer component={ Paper } variant="outlined">
                                <Table stickyHeader>
                                    <TableHead>
                                        <TableRow sx={ { '& th': { backgroundColor: 'slate.50', fontWeight: 600 } } }>
                                            <TableCell>Produkt</TableCell>
                                            <TableCell align="center" sx={ { width: 120 } }>Ilość</TableCell>
                                            <TableCell align="center">Partia</TableCell>
                                            <TableCell align="center">Data ważności</TableCell>
                                            <TableCell align="right">Cena netto</TableCell>
                                            <TableCell align="right">Cena brutto</TableCell>
                                            <TableCell align="center">Akcje</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        <FieldArray name="productBatches">
                                            { () => (
                                                formik?.values?.productBatches?.map((item, index) => (
                                                    <TableRow
                                                        key={ index }
                                                        hover
                                                        sx={ { display: item?.quantity === 0 ? 'none' : 'table-row' } }
                                                    >
                                                        <TableCell sx={ { fontWeight: 500 } }>{ item?.name }</TableCell>
                                                        <TableCell align="center">
                                                            <TextFieldFormik
                                                                name={ `productBatches.${ index }.quantity` }
                                                                type="number"
                                                                size="small"
                                                                inputProps={ {
                                                                    min: '0',
                                                                    max: item?.maxQuantity,
                                                                    step: '1'
                                                                } }
                                                                required
                                                            />
                                                        </TableCell>
                                                        <TableCell align="center">{ item?.productBatchId || '-' }</TableCell>
                                                        <TableCell align="center">
                                                            { moment(item?.expiryDate, MISC_DATE_ISO_FORMAT).format(MISC_DATE_POLISH_DATE_ONLY_FORMAT) }
                                                        </TableCell>
                                                        <TableCell align="right">{ item?.netPrice } PLN</TableCell>
                                                        <TableCell align="right" sx={ { fontWeight: 600 } }>{ item?.grossPrice } PLN</TableCell>
                                                        <TableCell align="center">
                                                            <Tooltip title="Usuń pozycję">
                                                                <IconButton
                                                                    onClick={ () => onRemoveProduct(formik, index) }
                                                                >
                                                                    <RemoveCircleIcon/>
                                                                </IconButton>
                                                            </Tooltip>
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            ) }
                                        </FieldArray>

                                        { activeBatches.length > 0 && (
                                            <TableRow sx={ { '& td': { fontWeight: 700, backgroundColor: 'slate.50' } } }>
                                                <TableCell align="right">Podsumowanie:</TableCell>
                                                <TableCell align="center">{ totalQuantity }</TableCell>
                                                <TableCell colSpan={ 2 }></TableCell>
                                                <TableCell align="right">{ totalNet.toFixed(2) } PLN</TableCell>
                                                <TableCell align="right">{ totalGross.toFixed(2) } PLN</TableCell>
                                                <TableCell></TableCell>
                                            </TableRow>
                                        ) }
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Stack>

                        <BottomActionBar>
                            <SubmitButton
                                isLoading={ confirmDeliveryDocument?.loading || updateAndConfirmDeliveryDocument?.loading }>
                                Zatwierdź dokument
                            </SubmitButton>
                        </BottomActionBar>
                    </Box>
                )
            } }
        </Formik>
    );
};

export default DetailsForm;